import React, { useState, useEffect } from 'react';

const insights = [
    "Support throughout your stay...",
    "Provides in-stay information...",
    "Assists when issues arise...",
    "Everything you need. Before you ask."
];

const WelcomePage = ({ hotelName, guestName = 'Guest', onContinue, onStartChat, onLogout }) => {
    const hour = new Date().getHours();
    // Changed "Good afternoon" range to also say "Good morning"
    const greeting = hour < 17 ? 'Good morning' : 'Good evening';

    const discoveryOptions = [
        { label: "Connect to Wi-Fi", query: "What is the Wi-Fi network name and password?" },
        { label: "Check-in instructions", query: "Check-in instructions" },
        { label: "Lockbox access", query: "How do I access the lockbox?" },
        { label: "House essentials", query: "What house essentials are available in the property?" }
    ];

    const [displayedText, setDisplayedText] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);
    const [insightIndex, setInsightIndex] = useState(0);

    useEffect(() => {
        const currentInsight = insights[insightIndex];

        let timer;
        if (isDeleting) {
            timer = setTimeout(() => {
                setDisplayedText(currentInsight.substring(0, displayedText.length - 1));

                // Very light haptic feedback on delete
                try {
                    if (typeof navigator !== 'undefined' && navigator.vibrate) {
                        navigator.vibrate(2); // 2ms extremely light vibration
                    }
                } catch (e) { }

                if (displayedText.length <= 1) {
                    setIsDeleting(false);
                    setInsightIndex((prev) => (prev + 1) % insights.length);
                }
            }, 30);
        } else {
            if (displayedText === currentInsight) {
                timer = setTimeout(() => {
                    setIsDeleting(true);
                }, 1000); // Wait 1s before deleting
            } else {
                const nextChar = currentInsight[displayedText.length];
                let typeSpeed = 30 + Math.random() * 20; // Ultra realistic typing speed

                if (nextChar === ' ' || nextChar === ',' || nextChar === '.') {
                    typeSpeed += 5; // slight pause on punctuation
                }

                if (displayedText.length === 0) {
                    typeSpeed = 200; // start delay
                }

                timer = setTimeout(() => {
                    setDisplayedText(currentInsight.substring(0, displayedText.length + 1));

                    // Trigger a tiny haptic feedback when typing, if the device/browser supports it
                    try {
                        if (typeof navigator !== 'undefined' && navigator.vibrate) {
                            navigator.vibrate(2); // 2ms light vibration (reduced from 5ms)
                        }
                    } catch (e) {
                        // Ignore errors if the browser blocks it without user interaction
                    }
                }, typeSpeed);
            }
        }

        return () => clearTimeout(timer);
    }, [displayedText, isDeleting, insightIndex]);


    return (
        <div className="welcome-shell h-[100dvh] bg-[#f5f5f7] flex flex-col safe-top safe-bottom relative overflow-hidden">
            {/* Immersive background accents */}
            <div className="fixed top-[-5%] right-[-10%] w-[400px] h-[400px] bg-blue-200/20 rounded-full blur-[100px] pointer-events-none"></div>
            <div className="fixed bottom-[-10%] left-[-10%] w-[350px] h-[350px] bg-indigo-200/10 rounded-full blur-[80px] pointer-events-none"></div>

            {/* Logout Button */}
            <div className="welcome-logout absolute top-4 right-4 z-20 animate-fade-up">
                <button
                    onClick={onLogout}
                    className="welcome-logout-button w-10 h-10 flex items-center justify-center rounded-full bg-black/[0.03] border border-black/[0.02] text-black/20 hover:text-black/40 hover:bg-black/[0.05] active:scale-95 transition-all"
                    aria-label="Logout"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                </button>
            </div>

            {/* Main Content */}
            <main className="welcome-main flex-1 min-h-0 flex flex-col justify-start pt-12 px-8 pb-3 sm:pt-16 sm:p-8 z-10 max-w-lg mx-auto w-full">
                {/* Header/Greeting Area */}
                <section className="welcome-hero mb-8 sm:mb-12 animate-fade-up">
                    {/* Label positioned close to greeting with a small distance (mb-2) */}
                    <p className="welcome-label text-[10px] font-bold text-black/20 uppercase tracking-[0.3em] mb-2">Personal Concierge</p>
                    <h1 className="welcome-title text-[42px] font-bold text-[#1d1d1f] tracking-tight leading-[1.05]">
                        {greeting},<br />
                        <span className="text-black/25">{guestName}.</span>
                    </h1>
                    <div className="welcome-insight mt-6 flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-black/10 rounded-full shrink-0"></div>
                        <p
                            className="text-[13px] font-medium text-[#86868b] min-h-[20px] flex items-center"
                        >
                            {displayedText}
                            <span className="inline-block w-[1.5px] h-[14px] bg-[#86868b] ml-[2px] [animation:blink_1s_step-end_infinite]"></span>
                        </p>
                    </div>
                </section>

                {/* Main CTA */}
                <section className="welcome-cta-section mb-8 sm:mb-10 animate-fade-up" style={{ animationDelay: '100ms' }}>
                    <button
                        onClick={() => onStartChat()}
                        className="welcome-cta-button group relative w-full py-6 sm:py-7 bg-[#1d1d1f] text-white rounded-[2.5rem] shadow-[0_20px_40px_-10px_rgba(0,0,0,0.3)] flex flex-col items-center justify-center gap-3 sm:gap-4 active:scale-[0.97] transition-all overflow-hidden"
                    >
                        <div
                            className="absolute inset-0 bg-gradient-to-tr from-blue-600/20 via-transparent to-indigo-600/20"
                            style={{ animation: 'pulse-glow 6s ease-in-out infinite' }}
                        ></div>

                        <div className="flex items-center gap-3 relative z-10">
                            <span className="welcome-cta-label text-2xl font-bold tracking-tight">Ask Anything</span>
                            <svg className="welcome-cta-icon w-8 h-8 text-white/90 animate-[pulse_3s_cubic-bezier(0.4,0,0.6,1)_infinite]" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 2L14.4 9.6L22 12L14.4 14.4L12 22L9.6 14.4L2 12L9.6 9.6L12 2Z" />
                            </svg>
                        </div>

                        <div className="flex gap-1.5 opacity-20 relative z-10">
                            <div className="w-2 h-2 bg-white rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                            <div className="w-2 h-2 bg-white rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                            <div className="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                        </div>

                        <div
                            className="absolute top-0 -inset-full h-full w-1/2 z-5 block transform -skew-x-12 bg-gradient-to-r from-transparent via-white/10 to-transparent"
                            style={{ animation: 'shine-loop 5s ease-in-out infinite' }}
                        />
                    </button>
                </section>

                {/* Quick Discovery Section */}
                <section className="welcome-discovery animate-fade-up" style={{ animationDelay: '150ms' }}>
                    <div className="welcome-discovery-heading mb-6 text-center">
                        <h2 className="text-[11px] font-bold text-black/20 uppercase tracking-[0.3em] inline-block relative">
                            Quick Discovery
                            <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-4 h-[1px] bg-black/5"></div>
                        </h2>
                    </div>
                    <div className="welcome-chips flex flex-wrap items-center justify-center gap-2 mb-6 sm:mb-8">
                        {discoveryOptions.map((option, idx) => (
                            <button
                                key={idx}
                                onClick={() => onStartChat(option.query)}
                                className="welcome-chip px-5 py-3 rounded-2xl bg-white border border-black/[0.03] text-[12px] font-semibold text-[#1d1d1f]/60 hover:text-black hover:border-black/10 hover:shadow-sm active:scale-95 transition-all"
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>

                    {/* Supportive Footer Text */}
                    <div className="welcome-support text-center space-y-2">
                        <p className="text-[12px] font-medium text-black/40">
                            Here whenever you need help during your stay.
                        </p>
                        <div className="flex items-center justify-center gap-2">
                            <div className="w-1 h-1 bg-green-500 rounded-full animate-pulse"></div>
                            <p className="text-[10px] font-bold text-black/20 uppercase tracking-widest">
                                Available 24/7
                            </p>
                        </div>
                    </div>
                </section>
            </main>

            <footer className="welcome-footer px-8 pt-0 pb-5 sm:p-8 text-center animate-fade-up" style={{ animationDelay: '300ms' }}>
                <p className="text-[9px] font-bold text-black/10 tracking-[0.4em] uppercase">Refining the art of travel</p>
            </footer>

            <style>{`
        @media (max-height: 700px) {
          .welcome-main {
            padding-top: 44px;
            padding-bottom: 4px;
          }
          .welcome-logout {
            top: 12px;
            right: 12px;
          }
          .welcome-hero {
            margin-bottom: 24px;
          }
          .welcome-insight {
            margin-top: 18px;
          }
          .welcome-cta-section {
            margin-bottom: 24px;
          }
          .welcome-cta-button {
            padding-top: 20px;
            padding-bottom: 20px;
            gap: 10px;
          }
          .welcome-discovery-heading {
            margin-bottom: 18px;
          }
          .welcome-chips {
            margin-bottom: 18px;
          }
          .welcome-chip {
            padding: 10px 18px;
          }
          .welcome-footer {
            padding-bottom: 12px;
          }
        }

        @media (max-height: 620px) {
          .welcome-main {
            padding-top: 38px;
            padding-left: 28px;
            padding-right: 28px;
          }
          .welcome-logout-button {
            width: 36px;
            height: 36px;
          }
          .welcome-label {
            font-size: 9px;
            margin-bottom: 6px;
          }
          .welcome-title {
            font-size: 38px;
          }
          .welcome-hero {
            margin-bottom: 18px;
          }
          .welcome-insight {
            margin-top: 14px;
          }
          .welcome-cta-section {
            margin-bottom: 18px;
          }
          .welcome-cta-button {
            padding-top: 17px;
            padding-bottom: 17px;
            border-radius: 2rem;
          }
          .welcome-cta-label {
            font-size: 21px;
          }
          .welcome-cta-icon {
            width: 28px;
            height: 28px;
          }
          .welcome-discovery-heading {
            margin-bottom: 14px;
          }
          .welcome-chips {
            gap: 8px;
            margin-bottom: 12px;
          }
          .welcome-chip {
            padding: 9px 15px;
            font-size: 11px;
            border-radius: 14px;
          }
          .welcome-support {
            display: none;
          }
          .welcome-footer {
            padding-bottom: 10px;
          }
        }

        @media (max-height: 560px) {
          .welcome-main {
            padding-top: 32px;
          }
          .welcome-title {
            font-size: 34px;
          }
          .welcome-insight {
            margin-top: 10px;
          }
          .welcome-hero,
          .welcome-cta-section {
            margin-bottom: 14px;
          }
          .welcome-cta-button {
            padding-top: 14px;
            padding-bottom: 14px;
          }
          .welcome-discovery-heading {
            margin-bottom: 10px;
          }
          .welcome-footer {
            display: none;
          }
        }

        @keyframes shine-loop {
          0% { left: -100%; }
          40% { left: 150%; }
          100% { left: 150%; }
        }
        @keyframes pulse-glow {
          0%, 100% { opacity: 0.2; }
          50% { opacity: 0.6; }
        }
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
      `}</style>
        </div>
    );
};

export default WelcomePage;
